import { yupResolver } from '@hookform/resolvers/yup'
import { find, map } from 'lodash-es'
import { useEffect, useState } from 'react'
import { useFieldArray, useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import * as yup from 'yup'

import { Header } from '@/components'
import SelectField from '@/components/select-field'
import { Button, Input } from '@/components/ui'
import {
  Currency,
  RequestInvoice,
  RequestInvoicePayload,
} from '../../../common/models'
import {
  SettingsBaseCountry,
  SettingsCountryPayload,
} from '../../../common/models/settings'
import FormGroup from '../../../core/components/FormGroup'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import {
  getCurrencies,
  getSettingsCountries,
  updateSettingsCountries,
} from '../../../core/services/festival-settings'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { FinancingSettingsSection } from './settings-components/FinancingSettings'
import { TaxCategoriesSection } from './settings-components/TaxCategoriesSection'
import { useAppSelector } from '@/store.ts'
import { untoldEmails } from '@/core/constants'

export interface ICountriesValue {
  content: {
    currency: string
    country: string
  }[]
  dafaultCountry?: string
}

export interface IRequestInvoiceValue {
  email?: string
}

export const FinancialPage: React.FC = () => {
  const { editionId, festivalName } = useParams()
  const [removeCountryFromList, setRemoveCountryFromList] = useState<number>(0)
  const [
    showDeleteConfirmationModalCountries,
    setDeleteConfirmationModalCountries,
  ] = useState<boolean>(false)
  const [currencies, setCurrencies] = useState<Currency[]>()
  const [countries, setCountries] = useState<SettingsBaseCountry[]>([])
  const [countriesDefault, setCountriesDefault] = useState<string>()
  const [requestInvoice, setRequestInvoice] = useState<RequestInvoice>({
    email: '',
  })

  const [isError, setIsError] = useState<boolean>(false)
  const [errorStatus, setErrorStatus] = useState<number | undefined>(undefined)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const { hasEditPermission } = useCheckPermission({ page: 'SETTINGS' })

  useEffect(() => {
    // prettier-ignore
    (async () => {
      try {
        const response = await getCurrencies()
        setCurrencies(response.data.currencies)

        const response2 = await getSettingsCountries()
        setCountries(response2.data.countries)
        setCountriesDefault(response2.data.defaultCountry.id)

        const data3: RequestInvoice = {
          email: '',
        }

        setRequestInvoice(data3)

        setValueCountry('dafaultCountry', response2.data.defaultCountry.id)
        const content = map(response2.data.countries, c => { return ({ currency: c.currency?.id, country: c.country.id }) })
        setTimeout(() => {
          setValueCountry('content', content)
        }, 100)

        setValueRequestInvoice('email', data3.email)
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }, [editionId, festivalName])

  const schemaCountries = yup.object().shape({
    content: yup
      .array()
      .of(
        yup.object().shape({
          currency: yup.string().required(),
          country: yup.string().required(),
        }),
      )
      .required(),
    dafaultCountry: yup.string(),
  })

  const schemaRequestInvoice = yup.object().shape({
    email: yup.string(),
  })

  const {
    handleSubmit: handleSubmitCountry,
    register: registerCountry,
    formState: { errors: errorsCountry },
    setValue: setValueCountry,
    control: countryControl,
  } = useForm<ICountriesValue>({
    mode: 'onChange',
    resolver: yupResolver<ICountriesValue>(schemaCountries),
  })

  useEffect(() => {
    setValueCountry('dafaultCountry', countries?.[0]?.id)
  }, [countries?.[0]])

  const {
    fields: countryFields,
    append: appendCounty,
    remove: removeCountry,
  } = useFieldArray({
    control: countryControl,
    name: 'content',
  })

  const submitCountry = async (values: ICountriesValue) => {
    // prettier-ignore
    (async () => {
      const data: SettingsCountryPayload = {
        defaultCountry: {
          id: values.dafaultCountry,
          currency: {
            id: find(countries, c => c.id === values.dafaultCountry)?.currency?.id,
            name: find(countries, c => c.id === values.dafaultCountry)?.currency?.name || '',
            symbol: find(countries, c => c.id === values.dafaultCountry)?.currency?.symbol,
            exchangeRate: find(countries, c => c.id === values.dafaultCountry)?.currency?.exchangeRate || 0,
            default: find(countries, c => c.id === values.dafaultCountry)?.currency?.default,
          },
          country: {
            id: find(countries, c => c.id === values.dafaultCountry)?.country?.id,
            name: find(countries, c => c.id === values.dafaultCountry)?.country?.name,
            isoCode: find(countries, c => c.id === values.dafaultCountry)?.country?.isoCode,
            phonePrefix: find(countries, c => c.id === values.dafaultCountry)?.country?.phonePrefix,
          },
          default: find(countries, c => c.id === values.dafaultCountry)?.default,
        },
        countries: []
      }
      try {
        const { status } = await updateSettingsCountries(data)
        if (status === 200) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Update Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }

  const cancelCountry = async () => {
    const content = map(countries, (c) => {
      return { currency: c.currency.id, country: c.country.id }
    })
    setValueCountry('content', content)
    setValueCountry('dafaultCountry', countriesDefault)
  }

  const deleteCountry = () => {
    removeCountry(removeCountryFromList)
  }

  const {
    handleSubmit: handleRequestInvoice,
    register: registerRequestInvoice,
    formState: { errors: errorsRequestInvoice },
    setValue: setValueRequestInvoice,
  } = useForm<IRequestInvoiceValue>({
    mode: 'onChange',
    resolver: yupResolver<IRequestInvoiceValue>(schemaRequestInvoice),
    defaultValues: {
      email: '',
    },
  })

  const submitRequestInvoice = async (values: IRequestInvoiceValue) => {
    // prettier-ignore
    (async () => {
      const data: RequestInvoicePayload = {
        email: values.email,
      }
      try {
        console.log(data) //TODO replace with the submit api call
        // const { status } = updateFinancial(data)
        // if (status === 200) {
        //   setIsError(false)
        //   setShowMessageBox(true)
        //   setMessage('Update Successful')
        // }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
          setErrorStatus((e as { response?: { status?: number } })?.response?.status)
        } else {
          console.error(e)
        }
      }
    })()
  }

  const cancelRequestInvoice = async () => {
    setValueRequestInvoice('email', requestInvoice.email)
  }

  const { user } = useAppSelector((s) => s.app)

  return (
    <>
      <Header pageTitle="Settings" pageSubtitle="Financial Settings" />
      <div className="max-h-[calc(100vh-110px)] overflow-y-auto">
        {!untoldEmails.includes(user?.email ?? '') && (
          <FinancingSettingsSection />
        )}

        {!untoldEmails.includes(user?.email ?? '') && <TaxCategoriesSection />}

        <div className="rounded-3xl border border-monochrome-120 bg-gradient-4 mt-[25px]">
          <div className="p-[25px]">
            <span className="text-title-xxs text-monochrome-1"> Countries</span>
            <form
              id="settings-financial-countries-form"
              onSubmit={handleSubmitCountry(submitCountry)}
            >
              <div className="flex flex-col gap-3">
                <div className="flex flex-col items-end gap-3">
                  {map(countryFields, (field, index) => {
                    return (
                      <div
                        key={field.id}
                        className="rounded-3xl border border-monochrome-120 bg-gradient-4 w-full p-[25px] mt-[25px] space-y-[25px]"
                      >
                        <FormGroup label="Country">
                          <SelectField
                            outline
                            options={map(countries, (country) => ({
                              value: country.country.id,
                              label: country.country.name,
                            }))}
                            value={field.country}
                            onValueChange={(value) => {
                              setValueCountry(`content.${index}.country`, value)
                            }}
                            triggerClassName="!h-[44px] rounded-[16px]"
                            placeholder="Select Country"
                            disabled={!hasEditPermission}
                            error={
                              errorsCountry?.content?.[index]?.country?.message
                            }
                          />
                        </FormGroup>
                        <FormGroup label="Default Currency">
                          <SelectField
                            options={map(currencies, (currency) => ({
                              value: currency.id,
                              label: currency.name,
                            }))}
                            outline
                            value={field.currency}
                            onValueChange={(value) => {
                              setValueCountry(
                                `content.${index}.currency`,
                                value,
                              )
                            }}
                            triggerClassName="!h-[44px] rounded-[16px]"
                            placeholder="Select Currency"
                            disabled={!hasEditPermission}
                            error={
                              errorsCountry?.content?.[index]?.currency?.message
                            }
                          />
                        </FormGroup>
                        {countryFields.length > 0 && (
                          <Button
                            onClick={() => {
                              setDeleteConfirmationModalCountries(true)
                              setRemoveCountryFromList(index)
                            }}
                            variant="destructive"
                            disabled={!hasEditPermission}
                          >
                            Delete
                          </Button>
                        )}
                      </div>
                    )
                  })}
                  <Button
                    variant="secondary"
                    className="w-full"
                    onClick={() =>
                      appendCounty({
                        currency: '',
                        country: '',
                      })
                    }
                    disabled={!hasEditPermission}
                  >
                    Add New
                  </Button>
                </div>
                {countryFields.length > 0 && countries?.[0]?.id !== '' && (
                  <FormGroup label="Default Country">
                    <SelectField
                      options={map(countries, (country) => ({
                        value: country.id,
                        label: country.country.name,
                      }))}
                      value={countriesDefault}
                      onValueChange={(value) => {
                        setValueCountry('dafaultCountry', value)
                      }}
                      triggerClassName="!h-[44px] rounded-[16px]"
                      outline
                      placeholder="Select Default Country"
                      disabled={!hasEditPermission}
                      error={errorsCountry?.dafaultCountry?.message}
                    />
                  </FormGroup>
                )}
              </div>
            </form>
          </div>
          <div className="flex justify-end gap-2 p-3 border-t border-monochrome-120">
            <Button
              onClick={cancelCountry}
              variant="secondary"
              data-dismiss="modal"
              disabled={!hasEditPermission}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              form="settings-financial-countries-form"
              type="submit"
              disabled={!hasEditPermission}
            >
              Save
            </Button>
          </div>
        </div>

        <div className="rounded-3xl border border-monochrome-120 bg-gradient-4 mt-[25px]">
          <div className="p-[25px]">
            <span className="text-title-xxs text-monochrome-1">
              Request Invoice
            </span>
            <form
              id="settings-financial-request-invoice-form"
              onSubmit={handleRequestInvoice(submitRequestInvoice)}
              className="mt-[20px]"
            >
              <div className="flex flex-col items-start gap-3">
                <FormGroup label="Request Invoice Email">
                  <Input
                    isInvalid={!!errorsRequestInvoice?.email?.message}
                    {...registerRequestInvoice('email')}
                    type="text"
                    placeholder="Enter Request Invoice Email"
                    disabled={!hasEditPermission}
                  />
                </FormGroup>
              </div>
            </form>
          </div>
          <div className="flex justify-end gap-2 p-3 border-t border-monochrome-120">
            <Button
              onClick={cancelRequestInvoice}
              variant="secondary"
              data-dismiss="modal"
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              form="settings-financial-request-invoice-form"
              type="submit"
            >
              Save
            </Button>
          </div>
        </div>
      </div>
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModalCountries}
        setDeleteConfirmationModal={setDeleteConfirmationModalCountries}
        deleteTitle={'Country'}
        deleteFunc={deleteCountry}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
        errorStatus={errorStatus}
      />
    </>
  )
}
